import {
  Button,
  ErrorState,
  Skeleton,
  TableControls,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TOUCH,
  ToggleGroup,
  ToggleGroupItem,
} from "@nymbl/nos2-rules";
import { Star } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { usePanelMemory } from "@/components/calendar/CalendarPanel";
import { CalendarSheet } from "@/components/calendar/CalendarSheet";
import { EntriesList } from "@/components/time/EntriesList";
import { FavoritesDialog } from "@/components/time/FavoritesDialog";
import { LogTimeDialog, type LogTimeMode } from "@/components/time/LogTimeDialog";
import { SignOffBanner } from "@/components/time/SignOffBanner";
import { SignOffDialog } from "@/components/time/SignOffDialog";
import { WeekCards, type WeekPhase } from "@/components/time/WeekCards";
import { WeekGrid } from "@/components/time/WeekGrid";
import { WeekHeader } from "@/components/time/WeekHeader";
import { useCalendarWeek } from "@/hooks/use-calendar";
import { useRememberedText } from "@/hooks/use-remembered-text";
import { useSetFavorite } from "@/hooks/use-time-assignable";
import { useSignOffs } from "@/hooks/use-time-signoffs";
import { useMonthEntries, useTimeWeek } from "@/hooks/use-time-week";
import { ApiError } from "@/lib/time/api-client";
import {
  addDays,
  addMonths,
  entriesMonthOf,
  formatMonth,
  type IsoDate,
  monthStartOf,
  todayInZone,
  weekStartOf,
} from "@/lib/time/dates";
import type { GridCell, GridRow, Suggestion } from "@/lib/time/grid";
import {
  hasReadyMonth,
  onTimePercent,
  openPastWeeks,
  revokeBlock,
  signableWeeks,
  signedOffMonthOf,
} from "@/lib/time/signoff-rules";
import { cn } from "@/lib/utils";
import type { CalendarWeekView } from "@/schemas/calendar";
import { INVALID_TASK_LINK, type TimeSearch } from "@/schemas/time";

/** Grid / Entries segments: even width and room for "Entries (12)", 44 px tall on phones. */
const SEGMENT = cn(TOUCH, "min-w-24 px-4");

const NEW_LOG: { key: number; mode: LogTimeMode } = { key: 0, mode: { kind: "new" } };

/**
 * The From calendar button's count: the week's meetings to log when connected, null when the
 * calendar can be connected (the button opens Connect), undefined to hide the button.
 */
function calendarCountOf(view: CalendarWeekView | undefined): number | null | undefined {
  if (!view || view.connection === "unavailable") return undefined;
  return view.connection === "connected" ? view.meetings.length : null;
}

const isForbidden = (err: unknown) => err instanceof ApiError && err.status === 403;

const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

interface TimeScreenProps {
  /** The page URL: week (undefined = this week), view, and which dialog is open. */
  search: TimeSearch;
  /** Merges into the URL; `replace` for closing a dialog (no history entry to come back to). */
  onSearchChange: (next: Partial<TimeSearch>, replace?: boolean) => void;
}

/** The Time page: week header, sign-off, cards, and the week as a grid or a list of entries. */
export function TimeScreen({ search, onSearchChange }: TimeScreenProps) {
  const weekParam = search.week;
  // `?day=` (the header's Today chip) opens that day's week with the day selected; moving
  // weeks drops it, so "This week" is never pulled back to the chip's day.
  const day = search.day;
  const onWeekChange = (next: IsoDate | undefined) =>
    onSearchChange({ week: next, day: undefined });
  const now = new Date();
  const today = todayInZone(now, browserZone());
  const thisWeek = weekStartOf(today);
  // Sign-off openness follows the server's clock (Eastern), not the browser's.
  const signOffToday = todayInZone(now);
  const weekStart = weekParam ?? (day ? weekStartOf(day) : thisWeek);
  const phase: WeekPhase =
    weekStart < thisWeek ? "past" : weekStart > thisWeek ? "future" : "current";
  // Grid / Entries and the open dialogs live in the URL, so an agent can deep-link and reload.
  const tab = search.view === "entries" ? "entries" : "grid";
  // Leaving Entries drops its Week | Month range: the grid is always one week.
  // A grid cell with several entries opens Entries filtered to its task and day (removable
  // chips); the tabs themselves open it unfiltered.
  const [cellFilter, setCellFilter] = useState<{ taskName: string; date: string } | null>(null);
  const setTab = (next: "grid" | "entries") => {
    setCellFilter(null);
    onSearchChange(
      next === "entries" ? { view: "entries" } : { view: undefined, range: undefined },
    );
  };
  const openCellEntries = (row: GridRow, cell: GridCell) => {
    setCellFilter({ taskName: row.taskName, date: cell.date });
    onSearchChange({ view: "entries" });
  };
  // Entries can list the whole month the shown week belongs to (`?range=month`).
  const monthView = tab === "entries" && search.range === "month";
  const setRange = (next: "week" | "month") =>
    onSearchChange({ range: next === "month" ? "month" : undefined });
  const [gridQuery, setGridQuery] = useRememberedText<string>("dashboard:time-search", "");
  // Copy and Edit carry an entry, so they stay in component state; a new entry is `?log=open`.
  const [entryLog, setEntryLog] = useState<{ key: number; mode: LogTimeMode } | null>(null);
  // `?log=open&task=` is a shared task link: Log time opens on that task (the person's own
  // assignment to it is looked up in the dialog).
  const linkedTask = search.task;
  const urlLog = linkedTask
    ? {
        key: 1,
        mode: {
          kind: "link",
          projectTaskId: linkedTask === INVALID_TASK_LINK ? null : linkedTask,
        } satisfies LogTimeMode,
      }
    : NEW_LOG;
  const log = entryLog ?? (search.log === "open" ? urlLog : null);
  const setLog = (next: null) => {
    setEntryLog(next);
    if (search.log || search.task) onSearchChange({ log: undefined, task: undefined }, true);
  };
  // A suggestion opens Favorites pre-searched (component state); the plain button is the URL.
  const [favoritesQuery, setFavoritesQuery] = useState<{ key: number; query: string } | null>(null);
  const favorites = favoritesQuery ?? (search.favorites === "open" ? { key: 0, query: "" } : null);
  const setFavorites = (next: { key: number; query: string } | null) => {
    if (next && next.query === "") {
      setFavoritesQuery(null);
      onSearchChange({ favorites: "open" });
      return;
    }
    setFavoritesQuery(next);
    if (!next && search.favorites) onSearchChange({ favorites: undefined }, true);
  };
  const meetingsOpen = search.meetings === "open";
  const setMeetingsOpen = (open: boolean) =>
    onSearchChange({ meetings: open ? "open" : undefined }, !open);
  const signOffOpen = search.signoff === "open";
  const setSignOffOpen = (open: boolean) =>
    onSearchChange({ signoff: open ? "open" : undefined }, !open);

  // Each meeting's Accept entry id, kept while the Time page is open (Accept retry safety).
  const calendarEntryIds = useRef(new Map<string, string>()).current;
  // A failed Connect calendar comes back with `?calendar=error`: say so once, then drop it.
  const calendarError = search.calendar === "error";
  useEffect(() => {
    if (!calendarError) return;
    toast.error("Calendar not connected", { id: "calendar-error" });
    onSearchChange({ calendar: undefined }, true);
  }, [calendarError, onSearchChange]);

  const week = useTimeWeek(weekStart);
  // The header's From calendar count; the sheet reads the same query (one request).
  const calendarWeek = useCalendarWeek(weekStart);
  const calendarFailed = calendarWeek.isError && !isForbidden(calendarWeek.error);
  // undefined hides the button; a failed read still offers it (the sheet says so, with Try again).
  const calendarCount = calendarFailed ? null : calendarCountOf(calendarWeek.data);
  const calendarMemory = usePanelMemory(weekStart);
  const entriesMonth = entriesMonthOf(weekStart);
  const monthLabel = formatMonth(entriesMonth);
  const month = useMonthEntries(entriesMonth, { enabled: monthView });
  const signOffs = useSignOffs();
  const favorite = useSetFavorite();

  // A failed sign-off load shows no count (never "All signed off").
  const signOffRows = signOffs.isError ? undefined : signOffs.data?.rows;
  const openWeekCount = signOffRows ? openPastWeeks(signOffRows, signOffToday).length : null;
  const hasSignable = signOffRows
    ? signableWeeks(signOffRows, signOffToday).length > 0 ||
      hasReadyMonth(signOffRows, signOffToday)
    : false;
  const onTimePct = signOffRows ? onTimePercent(signOffRows, now) : null;
  const shownWeekRow = signOffRows?.find((r) => r.periodType === "week" && r.start === weekStart);
  const revocableWeek = shownWeekRow && revokeBlock(shownWeekRow) === null ? shownWeekRow : null;
  const revocableMonth =
    revocableWeek && signOffRows ? signedOffMonthOf(revocableWeek, signOffRows) : null;
  const favoriteCount = useMemo(
    () =>
      new Set(
        (week.data?.assignable ?? [])
          .filter((a) => a.isFavorite)
          .map((a) => a.projectTaskId.toUpperCase()),
      ).size,
    [week.data],
  );
  const totals = useMemo(() => {
    const entries = week.data?.entries ?? [];
    return {
      week: entries.reduce((a, e) => a + e.hours, 0),
      pto: entries.filter((e) => e.isPto).reduce((a, e) => a + e.hours, 0),
    };
  }, [week.data]);

  const goToWeek = (start: IsoDate) => onWeekChange(start === thisWeek ? undefined : start);
  // Month mode steps by month. The week it lands on is one that belongs to that month (this
  // week for the current month), so switching back to Week stays inside the month.
  const goToMonth = (first: IsoDate) =>
    goToWeek(entriesMonthOf(thisWeek) === first ? thisWeek : weekStartOf(first));
  const openLog = (mode: LogTimeMode) =>
    mode.kind === "new" ? onSearchChange({ log: "open" }) : setEntryLog({ key: Date.now(), mode });
  const defaultLogDate = weekStart === thisWeek ? today : weekStart;

  function onSuggestion(s: Suggestion) {
    const only = s.tasks.length === 1 ? s.tasks[0] : undefined;
    if (only) {
      favorite.mutate({ projectTaskId: only.projectTaskId, isFavorite: true });
      return;
    }
    setFavorites({ key: Date.now(), query: s.demandName });
  }

  // A grid cell's entries open filtered to its task and day; the chips clear them.
  const cellFilters = cellFilter
    ? {
        task: { type: "values" as const, values: [cellFilter.taskName] },
        date: { type: "dates" as const, from: cellFilter.date, to: cellFilter.date },
      }
    : undefined;
  const entriesKey = cellFilter ? `${cellFilter.taskName}|${cellFilter.date}` : "all";
  const entriesSearch = { value: gridQuery, onChange: setGridQuery };
  const viewTabs = (
    <TabsList aria-label="View">
      <TabsTrigger value="grid" className={SEGMENT}>
        Grid
      </TabsTrigger>
      <TabsTrigger value="entries" className={SEGMENT}>
        Entries (
        {monthView ? (month.data?.entries.length ?? "…") : (week.data?.entries.length ?? 0)})
      </TabsTrigger>
    </TabsList>
  );
  const favoritesButton = (
    <Button
      variant="outline"
      className={cn(TOUCH, "shrink-0")}
      onClick={() => setFavorites({ key: Date.now(), query: "" })}
    >
      <Star className="size-4 fill-primary text-primary" aria-hidden="true" />
      Favorites ({favoriteCount})
    </Button>
  );
  const entriesControls = (
    <>
      {viewTabs}
      <ToggleGroup
        type="single"
        variant="outline"
        aria-label="Entries range"
        value={monthView ? "month" : "week"}
        onValueChange={(v) => {
          if (v === "week" || v === "month") setRange(v);
        }}
      >
        <ToggleGroupItem value="week" className={cn(TOUCH, "px-3")}>
          Week
        </ToggleGroupItem>
        <ToggleGroupItem value="month" className={cn(TOUCH, "px-3")}>
          Month
        </ToggleGroupItem>
      </ToggleGroup>
      {favoritesButton}
    </>
  );

  return (
    <div className="flex w-full min-w-0 flex-col gap-6">
      <WeekHeader
        weekStart={weekStart}
        isCurrentWeek={weekStart === thisWeek}
        month={
          monthView
            ? { label: monthLabel, isCurrent: entriesMonth === monthStartOf(today) }
            : undefined
        }
        openWeekCount={openWeekCount}
        revocableWeek={revocableWeek}
        revocableMonth={revocableMonth}
        onPrev={() =>
          monthView ? goToMonth(addMonths(entriesMonth, -1)) : goToWeek(addDays(weekStart, -7))
        }
        onNext={() =>
          monthView ? goToMonth(addMonths(entriesMonth, 1)) : goToWeek(addDays(weekStart, 7))
        }
        onThisWeek={() => (monthView ? goToMonth(monthStartOf(today)) : onWeekChange(undefined))}
        onSignOff={() => setSignOffOpen(true)}
        hasSignable={hasSignable}
        onLogTime={() => openLog({ kind: "new" })}
        calendarCount={calendarCount}
        onCalendar={() => setMeetingsOpen(true)}
      />

      {week.isError && (
        <ErrorState
          message="Time didn't load"
          onRetry={() => {
            void week.refetch();
          }}
        />
      )}

      {/* The page body waits for the week AND the first sign-off load (a failure counts as
          loaded), so the banner and the on-time card never push the page down after paint. */}
      {!week.isError && (week.isPending || signOffs.isPending) && (
        <div className="space-y-3" aria-busy="true">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      )}

      {week.data && !week.isError && !signOffs.isPending && (
        <>
          <SignOffBanner count={openWeekCount ?? 0} onSignOff={() => setSignOffOpen(true)} />
          <WeekCards
            phase={phase}
            weekTotal={totals.week}
            ptoTotal={totals.pto}
            onTimePct={onTimePct}
          />
          <Tabs
            value={tab}
            onValueChange={(v) => setTab(v === "entries" ? "entries" : "grid")}
            className="min-w-0 gap-3"
          >
            <section
              aria-label={monthView ? monthLabel : "Your week"}
              className="min-w-0 space-y-3"
            >
              {tab === "grid" && (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="font-display text-lg font-bold">Your week</h2>
                    {viewTabs}
                  </div>
                  <TableControls value={gridQuery} onChange={setGridQuery} label="Search tasks">
                    {favoritesButton}
                  </TableControls>
                </div>
              )}
              <TabsContent value="grid" className="min-w-0">
                <WeekGrid
                  view={week.data}
                  assignable={week.data.assignable}
                  today={today}
                  selectedDay={day}
                  query={gridQuery}
                  onOpenEntries={openCellEntries}
                  onSuggestion={onSuggestion}
                />
              </TabsContent>
              <TabsContent value="entries" className="min-w-0">
                {!monthView && (
                  <EntriesList
                    key={entriesKey}
                    entries={week.data.entries}
                    assignable={week.data.assignable}
                    title="Your week"
                    controls={entriesControls}
                    initialFilters={cellFilters}
                    searchState={entriesSearch}
                    onCopy={(entry) => openLog({ kind: "copy", entry })}
                    onEdit={(entry) => openLog({ kind: "edit", entry })}
                  />
                )}
                {monthView && month.isError && (
                  <ErrorState
                    message={`${monthLabel} didn't load`}
                    onRetry={() => {
                      void month.refetch();
                    }}
                  />
                )}
                {monthView && month.isPending && (
                  <div aria-busy="true">
                    <Skeleton className="h-96 w-full" />
                  </div>
                )}
                {monthView && month.data && !month.isError && (
                  <EntriesList
                    key={entriesKey}
                    entries={month.data.entries}
                    assignable={week.data?.assignable ?? []}
                    period={{ kind: "month", label: monthLabel }}
                    title={monthLabel}
                    controls={entriesControls}
                    initialFilters={cellFilters}
                    searchState={entriesSearch}
                    onCopy={(entry) => openLog({ kind: "copy", entry })}
                    onEdit={(entry) => openLog({ kind: "edit", entry })}
                  />
                )}
              </TabsContent>
            </section>
          </Tabs>
        </>
      )}

      {log && (
        <LogTimeDialog
          key={log.key}
          open
          onOpenChange={(open) => {
            if (!open) setLog(null);
          }}
          mode={log.mode}
          defaultDate={defaultLogDate}
          today={today}
        />
      )}
      {favorites && (
        <FavoritesDialog
          key={favorites.key}
          open
          onOpenChange={(open) => {
            if (!open) setFavorites(null);
          }}
          initialQuery={favorites.query}
        />
      )}
      <CalendarSheet
        // With the button hidden (no calendar, an admin impersonating) `?meetings=open` opens nothing.
        open={
          meetingsOpen &&
          !(calendarWeek.isError && isForbidden(calendarWeek.error)) &&
          calendarWeek.data?.connection !== "unavailable"
        }
        onOpenChange={setMeetingsOpen}
        weekStart={weekStart}
        entryIds={calendarEntryIds}
        memory={calendarMemory}
      />
      <SignOffDialog
        open={signOffOpen}
        onOpenChange={setSignOffOpen}
        view={signOffs.data}
        error={signOffs.isError}
        onRetry={() => {
          void signOffs.refetch();
        }}
        today={signOffToday}
        now={now}
      />
    </div>
  );
}
